import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface ContactScore {
  contact_id: string;
  influence_score: number;
  engagement_score: number;
  responsiveness_score: number;
  overall_score: number;
  score_level: 'high' | 'medium' | 'low';
  last_calculated: string;
  factors: {
    position_weight: number;
    activity_frequency: number;
    response_time: number;
    meeting_attendance: number;
    email_engagement: number;
  };
}

export interface ContactActivity {
  id: string;
  contact_id: string;
  activity_type: 'email' | 'call' | 'meeting' | 'note' | 'task' | 'document';
  title: string;
  description?: string;
  direction?: 'inbound' | 'outbound';
  status: 'completed' | 'pending' | 'cancelled';
  due_date?: string;
  completed_at?: string;
  created_at: string;
  created_by: string;
  metadata?: Record<string, any>;
}

class ContactScoringService {
  async calculateContactScore(contactId: string): Promise<ContactScore> {
    try {
      const contact = await this.getContactDetails(contactId);
      const activities = await this.getContactActivities(contactId);
      const factors = this.calculateScoringFactors(contact, activities);
      const scores = this.calculateScores(factors);
      const overallScore = this.calculateOverallScore(scores);
      const scoreLevel = this.determineScoreLevel(overallScore);

      const contactScore: ContactScore = {
        contact_id: contactId,
        influence_score: scores.influence,
        engagement_score: scores.engagement,
        responsiveness_score: scores.responsiveness,
        overall_score: overallScore,
        score_level: scoreLevel,
        last_calculated: new Date().toISOString(),
        factors,
      };

      await this.saveContactScore(contactScore);
      return contactScore;
    } catch (error) {
      logError('Error calculating contact score:', error);
      throw error;
    }
  }

  private async getContactDetails(contactId: string) {
    const { data, error } = await supabase
      .from('contacts')
      .select(
        `
        *,
        organizations!inner(tier, name)
      `
      )
      .eq('id', contactId)
      .single();

    if (error) throw error;
    return data;
  }

  private async getContactActivities(contactId: string, days = 90): Promise<ContactActivity[]> {
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('activities')
      .select('*')
      .eq('contact_id', contactId)
      .gte('created_at', startDate)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  private calculateScoringFactors(contact: any, activities: ContactActivity[]) {
    // Position weight based on job title and influence level
    const positionWeight = this.calculatePositionWeight(contact);

    // Activity frequency - how often we interact
    const activityFrequency = Math.min(100, activities.length * 2);

    // Response time calculation (mock data for now)
    const responseTime = this.calculateResponseTime(activities);

    // Meeting attendance rate
    const meetingAttendance = this.calculateMeetingAttendance(activities);

    // Email engagement (opens, clicks, responses)
    const emailEngagement = this.calculateEmailEngagement(activities);

    return {
      position_weight: positionWeight,
      activity_frequency: activityFrequency,
      response_time: responseTime,
      meeting_attendance: meetingAttendance,
      email_engagement: emailEngagement,
    };
  }

  private calculatePositionWeight(contact: any): number {
    let weight = 50; // Base score

    // Influence level scoring
    switch (contact.influence_level?.toLowerCase()) {
      case 'high':
      case 'decision maker':
        weight += 30;
        break;
      case 'medium':
      case 'influencer':
        weight += 20;
        break;
      case 'low':
        weight += 10;
        break;
    }

    // Job title keywords that indicate seniority
    const seniorKeywords = ['ceo', 'president', 'director', 'vp', 'chief', 'head', 'manager'];
    const jobTitle = contact.job_title?.toLowerCase() || '';

    if (seniorKeywords.some((keyword) => jobTitle.includes(keyword))) {
      weight += 15;
    }

    // Organization tier impact
    switch (contact.organizations?.tier?.toLowerCase()) {
      case 'tier 1':
        weight += 20;
        break;
      case 'tier 2':
        weight += 10;
        break;
    }

    return Math.min(100, weight);
  }

  private calculateResponseTime(activities: ContactActivity[]): number {
    const emailActivities = activities.filter((a) => a.activity_type === 'email');
    if (emailActivities.length === 0) return 50;

    // Mock response time calculation - in real app, would track actual response times
    const avgResponseHours = 24; // Average response time in hours

    if (avgResponseHours <= 2) return 100;
    if (avgResponseHours <= 8) return 80;
    if (avgResponseHours <= 24) return 60;
    if (avgResponseHours <= 48) return 40;
    return 20;
  }

  private calculateMeetingAttendance(activities: ContactActivity[]): number {
    const meetings = activities.filter((a) => a.activity_type === 'meeting');
    if (meetings.length === 0) return 50;

    const attendedMeetings = meetings.filter((m) => m.status === 'completed');
    const attendanceRate = (attendedMeetings.length / meetings.length) * 100;

    return Math.round(attendanceRate);
  }

  private calculateEmailEngagement(activities: ContactActivity[]): number {
    const emails = activities.filter((a) => a.activity_type === 'email');
    if (emails.length === 0) return 50;

    // Mock engagement calculation - in real app, would track opens, clicks, responses
    const inboundEmails = emails.filter((e) => e.direction === 'inbound');
    const engagementRate =
      inboundEmails.length > 0 ? Math.min(100, (inboundEmails.length / emails.length) * 100) : 30;

    return Math.round(engagementRate);
  }

  private calculateScores(factors: ContactScore['factors']) {
    // Influence score based on position and organization
    const influence = Math.round(factors.position_weight);

    // Engagement score based on activity and email engagement
    const engagement = Math.round(
      factors.activity_frequency * 0.4 + factors.email_engagement * 0.6
    );

    // Responsiveness score based on response time and meeting attendance
    const responsiveness = Math.round(
      factors.response_time * 0.6 + factors.meeting_attendance * 0.4
    );

    return { influence, engagement, responsiveness };
  }

  private calculateOverallScore(scores: {
    influence: number;
    engagement: number;
    responsiveness: number;
  }): number {
    const weights = {
      influence: 0.4,
      engagement: 0.35,
      responsiveness: 0.25,
    };

    return Math.round(
      scores.influence * weights.influence +
        scores.engagement * weights.engagement +
        scores.responsiveness * weights.responsiveness
    );
  }

  private determineScoreLevel(score: number): ContactScore['score_level'] {
    if (score >= 75) return 'high';
    if (score >= 50) return 'medium';
    return 'low';
  }

  private async saveContactScore(score: ContactScore): Promise<void> {
    try {
      await supabase.from('contact_scores').insert({
        contact_id: score.contact_id,
        influence_score: score.influence_score,
        engagement_score: score.engagement_score,
        responsiveness_score: score.responsiveness_score,
        overall_score: score.overall_score,
        score_level: score.score_level,
        position_weight: score.factors.position_weight,
        activity_frequency: score.factors.activity_frequency,
        response_time: score.factors.response_time,
        meeting_attendance: score.factors.meeting_attendance,
        email_engagement: score.factors.email_engagement,
        created_at: score.last_calculated,
      });
    } catch (error) {
      logError('Error saving contact score:', error);
    }
  }

  async getContactScore(contactId: string): Promise<ContactScore | null> {
    try {
      const { data, error } = await supabase
        .from('contact_scores')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return {
        contact_id: data.contact_id,
        influence_score: data.influence_score,
        engagement_score: data.engagement_score,
        responsiveness_score: data.responsiveness_score,
        overall_score: data.overall_score,
        score_level: data.score_level,
        last_calculated: data.created_at,
        factors: {
          position_weight: data.position_weight,
          activity_frequency: data.activity_frequency,
          response_time: data.response_time,
          meeting_attendance: data.meeting_attendance,
          email_engagement: data.email_engagement,
        },
      };
    } catch (error) {
      logError('Error fetching contact score:', error);
      return null;
    }
  }

  async getTopContacts(
    limit = 10
  ): Promise<(ContactScore & { contact_name: string; organization_name: string })[]> {
    try {
      const { data, error } = await supabase
        .from('contact_scores')
        .select(
          `
          *,
          contacts!inner(first_name, last_name),
          contacts!inner(organizations!inner(name))
        `
        )
        .order('overall_score', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (
        data?.map((item) => ({
          contact_id: item.contact_id,
          contact_name: `${item.contacts.first_name} ${item.contacts.last_name}`,
          organization_name: item.contacts.organizations.name,
          influence_score: item.influence_score,
          engagement_score: item.engagement_score,
          responsiveness_score: item.responsiveness_score,
          overall_score: item.overall_score,
          score_level: item.score_level,
          last_calculated: item.created_at,
          factors: {
            position_weight: item.position_weight,
            activity_frequency: item.activity_frequency,
            response_time: item.response_time,
            meeting_attendance: item.meeting_attendance,
            email_engagement: item.email_engagement,
          },
        })) || []
      );
    } catch (error) {
      logError('Error fetching top contacts:', error);
      return [];
    }
  }

  async logContactActivity(
    activity: Omit<ContactActivity, 'id' | 'created_at'>
  ): Promise<ContactActivity> {
    try {
      const { data, error } = await supabase
        .from('contact_activities')
        .insert({
          ...activity,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Recalculate contact score after new activity
      await this.calculateContactScore(activity.contact_id);

      return data;
    } catch (error) {
      logError('Error logging contact activity:', error);
      throw error;
    }
  }

  async getContactActivityFeed(contactId: string, limit = 50): Promise<ContactActivity[]> {
    try {
      const { data, error } = await supabase
        .from('contact_activities')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logError('Error fetching contact activity feed:', error);
      return [];
    }
  }
}

export const contactScoringService = new ContactScoringService();
